const TYPE_LABELS = {
  hum: 'hum',
  fan_rumble: 'fan rumble',
  hiss: 'hiss',
  crowd: 'crowd',
  multi_speaker: 'multiple speakers (all kept)',
  screech: 'screech',
  reverb: 'reverb',
  clipping: 'clipping',
  quiet_speaker: 'quiet speaker',
  wind: 'wind',
  clean: 'clean',
};

const SOLUTION_LABELS = {
  silero: 'Silero VAD',
  notch: 'hum notch',
  spectral: 'spectral denoise',
  adaptive: 'adaptive denoise',
  deepfilternet: 'DeepFilterNet',
  demucs: 'Demucs',
  isolation_spectral: 'voice isolation',
  screech_gate: 'screech gate',
  dereverb: 'dereverb',
  spectral_isolate: 'spectral isolate',
  ffmpeg: 'FFmpeg',
};

export function labelNoiseType(type) {
  const key = String(type || '').trim().toLowerCase();
  return TYPE_LABELS[key] || key.replace(/_/g, ' ');
}

export function labelSolution(name) {
  const key = String(name || '').trim().toLowerCase();
  return SOLUTION_LABELS[key] || key.replace(/_/g, ' ');
}

function skippedNames(skipped = []) {
  return skipped
    .map((item) => (typeof item === 'string' ? item : item?.name))
    .filter(Boolean)
    .map(labelSolution);
}

export function snippetOriginalAudioUrl(snippet) {
  const source = String(snippet?.source_audio_s3_url || '').trim();
  if (source) return source;
  if (!snippet?.audio_cleaned) {
    return String(snippet?.audio_s3_url || snippet?.original_s3_url || '').trim();
  }
  return '';
}

export function snippetCleanedAudioUrl(snippet) {
  const explicit = String(snippet?.cleaned_audio_s3_url || '').trim();
  if (explicit) return explicit;
  const source = String(snippet?.source_audio_s3_url || '').trim();
  const current = String(snippet?.audio_s3_url || '').trim();
  if (snippet?.audio_cleaned && current && current !== source) return current;
  return '';
}

export function snippetAudioChoice(snippet) {
  const raw = String(snippet?.audio_choice || '').trim().toLowerCase();
  if (raw === 'original' || raw === 'source' || raw === 'extracted') return 'original';
  if (raw === 'cleaned' || raw === 'clean' || raw === 'enhanced') return 'cleaned';
  return snippetCleanedAudioUrl(snippet) ? 'cleaned' : 'original';
}

export function snippetSelectedAudioUrl(snippet) {
  if (snippetAudioChoice(snippet) === 'cleaned') {
    return snippetCleanedAudioUrl(snippet) || snippetOriginalAudioUrl(snippet);
  }
  return snippetOriginalAudioUrl(snippet) || snippetCleanedAudioUrl(snippet);
}

export function stampedAudioSrc(url, stamp = '') {
  const base = url || '';
  if (!base) return '';
  if (!stamp) return base;
  return `${base}${base.includes('?') ? '&' : '?'}t=${encodeURIComponent(stamp)}`;
}

export function snippetCleanSummary(snippet) {
  if (!snippet) return '';
  const types = (snippet.audio_noise_types || []).map(labelNoiseType).filter(Boolean);
  const solutions = (snippet.audio_clean_solutions || []).map(labelSolution).filter(Boolean);
  if (types.length && solutions.length) return `${types.join(', ')} → ${solutions.join(', ')}`;
  if (solutions.length) return solutions.join(', ');
  if (types.length) return types.join(', ');
  return snippet.audio_clean_preset ? String(snippet.audio_clean_preset) : '';
}

export function snippetCleanToast(snippet, filename = '') {
  const name = filename || snippet?.filename || 'snippet';
  const summary = snippetCleanSummary(snippet);
  const skipped = skippedNames(snippet?.audio_clean_skipped);
  let text = summary ? `Cleaned ${name} — ${summary}` : `Cleaned ${name}`;
  text += '. Compare the new MP3 under the original and pick which to use.';
  if (skipped.length) text += ` (${skipped.join(', ')} skipped)`;
  return text;
}

export function cleanAllToast(snippets = []) {
  const rows = (snippets || []).filter((s) => s?.audio_cleaned);
  const types = [...new Set(rows.flatMap((s) => s.audio_noise_types || []).map(labelNoiseType).filter(Boolean))];
  const solutions = [...new Set(rows.flatMap((s) => s.audio_clean_solutions || []).map(labelSolution).filter(Boolean))];
  const skipped = [...new Set(rows.flatMap((s) => skippedNames(s.audio_clean_skipped)))];
  let text = 'Cleaned extracted audio on every snippet';
  if (types.length && solutions.length) {
    text = `Detected ${types.join(', ')} → ${solutions.join(', ')}`;
  } else if (solutions.length) {
    text = `Cleaned with ${solutions.join(', ')}`;
  }
  text += '. Cleaned MP3 is under the original — pick which soundtrack to use.';
  if (skipped.length) text += ` (${skipped.join(', ')} skipped)`;
  return text;
}
